import { ApiError } from "./api-error";
import type { AdminSubscriptionFilters, PortalApi } from "./api";
import type {
  Address,
  AdminCatalogProduct,
  AdminOffer,
  AdminPlan,
  AdminPlanInput,
  AdminSettings,
  AdminSubscription,
  AuditEntry,
  CustomerSummary,
  IyzicoCatalogPricingPlan,
  IyzicoCatalogProduct,
  LegalDocument,
  OrderSyncIssue,
  PaymentIssue,
  PlanCatalog,
  Branding,
  CheckoutView,
} from "./schemas";

const customerUid = "shopify_7845129630";
const day = 86_400_000;
const isoFromNow = (days: number): string => new Date(Date.now() + days * day).toISOString();

const defaultAddress: Address = {
  firstName: "Deniz",
  lastName: "Yılmaz",
  phone: "05321234567",
  address1: "Fener Mahallesi, Narenciye Sokak No: 12",
  address2: "Daire 4",
  city: "Antalya",
  district: "Muratpaşa",
  province: "Antalya",
  postalCode: "07160",
  countryCode: "TR",
};

const plans: AdminPlan[] = [
  {
    id: "plan-tropik-5kg-monthly-v2",
    name: "Mevsimin Tropik Kutusu",
    description: "Dalından seçilen mevsim meyveleriyle her ay taze bir keşif.",
    variantLabel: "5 kg",
    sku: "TA-TROP-5",
    frequency: "MONTHLY",
    intervalCount: 1,
    amountMinor: 129_900,
    currency: "TRY",
    shippingIncluded: true,
    shippingPriceMinor: 10_000,
    shopifyProductId: "1001",
    shopifyProductGid: "gid://shopify/Product/1001",
    shopifyVariantId: "2002",
    shopifyVariantGid: "gid://shopify/ProductVariant/2002",
    iyzicoProductReferenceCode: "iyzi-product-tropik",
    iyzicoPricingPlanReferenceCode: "iyzi-plan-5kg-monthly",
    active: true,
    version: 2,
  },
  {
    id: "plan-tropik-3kg-monthly-v1",
    name: "Tropik Başlangıç Kutusu",
    description: "Daha küçük evler için dengeli, rengârenk bir seçki.",
    variantLabel: "3 kg",
    sku: "TA-TROP-3",
    frequency: "MONTHLY",
    intervalCount: 1,
    amountMinor: 89_900,
    currency: "TRY",
    shippingIncluded: true,
    shippingPriceMinor: 10_000,
    shopifyProductId: "1001",
    shopifyProductGid: "gid://shopify/Product/1001",
    shopifyVariantId: "2001",
    shopifyVariantGid: "gid://shopify/ProductVariant/2001",
    iyzicoProductReferenceCode: "iyzi-product-tropik",
    iyzicoPricingPlanReferenceCode: "iyzi-plan-3kg-monthly",
    active: true,
    version: 1,
  },
  {
    id: "plan-tropik-10kg-monthly-v1",
    name: "Paylaşmalık Tropik Kutu",
    description: "Kalabalık sofralara bol çeşitli, cömert bir kutu.",
    variantLabel: "10 kg",
    sku: "TA-TROP-10",
    frequency: "MONTHLY",
    intervalCount: 1,
    amountMinor: 209_900,
    currency: "TRY",
    shippingIncluded: true,
    shippingPriceMinor: 10_000,
    shopifyProductId: "1001",
    shopifyProductGid: "gid://shopify/Product/1001",
    shopifyVariantId: "2003",
    shopifyVariantGid: "gid://shopify/ProductVariant/2003",
    iyzicoProductReferenceCode: "iyzi-product-tropik",
    iyzicoPricingPlanReferenceCode: "iyzi-plan-10kg-monthly",
    active: false,
    version: 1,
  },
];

const shopifyProducts: PlanCatalog["shopifyProducts"] = [
  {
    id: "gid://shopify/Product/1001",
    title: "Tropikal Akdeniz Abonelik Kutusu",
    variants: [
      {
        id: "gid://shopify/ProductVariant/2001",
        title: "3 kg",
        sku: "TA-TROP-3",
        amountMinor: 79_900,
        currency: "TRY",
        availableForSale: true,
      },
      {
        id: "gid://shopify/ProductVariant/2002",
        title: "5 kg",
        sku: "TA-TROP-5",
        amountMinor: 119_900,
        currency: "TRY",
        availableForSale: true,
      },
      {
        id: "gid://shopify/ProductVariant/2003",
        title: "10 kg",
        sku: "TA-TROP-10",
        amountMinor: 199_900,
        currency: "TRY",
        availableForSale: false,
      },
    ],
  },
];

let iyzicoProducts: IyzicoCatalogProduct[] = [
  {
    referenceCode: "iyzi-product-tropik",
    name: "Tropikal Kutu Abonelikleri",
    description: "Aylık tropikal meyve kutuları",
    status: "ACTIVE",
  },
];

let iyzicoPricingPlans: IyzicoCatalogPricingPlan[] = [
  {
    referenceCode: "iyzi-plan-3kg-monthly",
    productReferenceCode: "iyzi-product-tropik",
    name: "3 kg aylık",
    amountMinor: 89_900,
    currency: "TRY",
    paymentInterval: "MONTHLY",
    paymentIntervalCount: 1,
    status: "ACTIVE",
  },
  {
    referenceCode: "iyzi-plan-5kg-monthly",
    productReferenceCode: "iyzi-product-tropik",
    name: "5 kg aylık",
    amountMinor: 129_900,
    currency: "TRY",
    paymentInterval: "MONTHLY",
    paymentIntervalCount: 1,
    status: "ACTIVE",
  },
  {
    referenceCode: "iyzi-plan-10kg-monthly",
    productReferenceCode: "iyzi-product-tropik",
    name: "10 kg aylık",
    amountMinor: 209_900,
    currency: "TRY",
    paymentInterval: "MONTHLY",
    paymentIntervalCount: 1,
    status: "ACTIVE",
  },
];

let subscriptions: AdminSubscription[] = [
  {
    id: "sub-tropik-001",
    customerUid,
    reference: "TA-ABN-2026-00184",
    status: "ACTIVE",
    providerStatus: "ACTIVE",
    plan: plans[0]!,
    amountMinor: 129_900,
    currency: "TRY",
    nextPaymentAt: isoFromNow(12),
    startedAt: isoFromNow(-49),
    shippingAddress: defaultAddress,
    lastOrder: {
      name: "#1847",
      customerUrl: "https://www.tropikalakdeniz.com/account/orders/mock-1847",
      adminUrl: "https://admin.shopify.com/store/mock/orders/1847",
      createdAt: isoFromNow(-18),
    },
    cycles: [
      {
        id: "cycle-003",
        cycleNumber: 3,
        paidAt: isoFromNow(-18),
        amountMinor: 129_900,
        currency: "TRY",
        paymentStatus: "SUCCEEDED",
        shopifySyncStatus: "SUCCEEDED",
        order: {
          name: "#1847",
          customerUrl: "https://www.tropikalakdeniz.com/account/orders/mock-1847",
          adminUrl: "https://admin.shopify.com/store/mock/orders/1847",
          createdAt: isoFromNow(-18),
        },
        attemptCount: 1,
      },
      {
        id: "cycle-002",
        cycleNumber: 2,
        paidAt: isoFromNow(-48),
        amountMinor: 129_900,
        currency: "TRY",
        paymentStatus: "SUCCEEDED",
        shopifySyncStatus: "SUCCEEDED",
        order: {
          name: "#1692",
          customerUrl: "https://www.tropikalakdeniz.com/account/orders/mock-1692",
          adminUrl: "https://admin.shopify.com/store/mock/orders/1692",
          createdAt: isoFromNow(-48),
        },
        attemptCount: 1,
      },
    ],
    customer: {
      name: "Deniz Yılmaz",
      email: "deniz@example.com",
      shopifyCustomerId: "7845129630",
    },
    webhookEvents: [
      {
        id: "evt-0291",
        eventType: "subscription.order.success",
        status: "PROCESSED",
        receivedAt: isoFromNow(-18),
      },
    ],
    auditEntries: [],
  },
  {
    id: "sub-tropik-002",
    customerUid,
    reference: "TA-ABN-2026-00209",
    status: "PAST_DUE",
    providerStatus: "UNPAID",
    plan: plans[1]!,
    amountMinor: 89_900,
    currency: "TRY",
    nextPaymentAt: isoFromNow(-2),
    startedAt: isoFromNow(-32),
    shippingAddress: defaultAddress,
    cycles: [
      {
        id: "cycle-failed-021",
        cycleNumber: 2,
        scheduledAt: isoFromNow(-2),
        amountMinor: 89_900,
        currency: "TRY",
        paymentStatus: "FAILED",
        shopifySyncStatus: "NOT_STARTED",
        attemptCount: 1,
        lastErrorCode: "PAYMENT_DECLINED",
        lastErrorMessage: "Ödeme kuruluşu işlemi onaylamadı.",
      },
    ],
    customer: {
      name: "Deniz Yılmaz",
      email: "deniz@example.com",
      shopifyCustomerId: "7845129630",
    },
    webhookEvents: [
      {
        id: "evt-0310",
        eventType: "subscription.order.failure",
        status: "PROCESSED",
        receivedAt: isoFromNow(-2),
      },
    ],
    auditEntries: [],
  },
  {
    id: "sub-other-customer",
    customerUid: "shopify_0000000001",
    reference: "TA-ABN-OTHER",
    status: "ACTIVE",
    providerStatus: "ACTIVE",
    plan: plans[0]!,
    amountMinor: 129_900,
    currency: "TRY",
    nextPaymentAt: isoFromNow(8),
    startedAt: isoFromNow(-20),
    shippingAddress: defaultAddress,
    cycles: [],
    customer: {
      name: "Başka Müşteri",
      email: "baska@example.com",
      shopifyCustomerId: "0000000001",
    },
    webhookEvents: [],
    auditEntries: [],
  },
];

let paymentIssues: PaymentIssue[] = [
  {
    cycleId: "cycle-failed-021",
    subscriptionId: "sub-tropik-002",
    customerName: "Deniz Yılmaz",
    planName: "Tropik Başlangıç Kutusu",
    errorCode: "PAYMENT_DECLINED",
    errorMessage: "Ödeme kuruluşu işlemi onaylamadı.",
    lastAttemptAt: isoFromNow(-2),
    retryAllowed: true,
    reviewRequired: false,
  },
  {
    cycleId: "cycle-ambiguous-022",
    subscriptionId: "sub-tropik-002",
    customerName: "Deniz Yılmaz",
    planName: "Tropik Başlangıç Kutusu",
    errorCode: "PAYMENT_RETRY_REVIEW_REQUIRED",
    errorMessage: "Sağlayıcı retry çağrısının sonucu alınamadı; yeni tahsilat olup olmadığı doğrulanmalı.",
    lastAttemptAt: isoFromNow(-1),
    retryAllowed: false,
    reviewRequired: true,
  },
];

let orderSyncIssues: OrderSyncIssue[] = [
  {
    cycleId: "cycle-sync-441",
    subscriptionId: "sub-tropik-001",
    orderReference: "IYZ-ORD-20260801-0441",
    errorMessage: "Shopify stok politikası sipariş satırını reddetti.",
    attemptCount: 3,
    lastAttemptAt: isoFromNow(-0.2),
    retryAllowed: true,
    canMarkPermanent: true,
  },
];

const customers: CustomerSummary[] = [
  {
    uid: customerUid,
    name: "Deniz Yılmaz",
    email: "deniz@example.com",
    shopifyCustomerId: "7845129630",
    status: "ACTIVE",
    activeSubscriptionCount: 1,
    lastLoginAt: isoFromNow(-0.1),
  },
  {
    uid: "shopify_0000000001",
    name: "Başka Müşteri",
    email: "baska@example.com",
    shopifyCustomerId: "0000000001",
    status: "ACTIVE",
    activeSubscriptionCount: 1,
    lastLoginAt: isoFromNow(-4),
  },
];

const auditEntries: AuditEntry[] = [
  {
    id: "audit-1004",
    actorType: "SYSTEM",
    actorLabel: "Order sync görevi",
    action: "SHOPIFY_ORDER_SYNC_FAILED",
    resourceType: "subscriptionCycle",
    resourceId: "cycle-sync-441",
    correlationId: "cor_2f384e49",
    createdAt: isoFromNow(-0.2),
  },
  {
    id: "audit-1003",
    actorType: "CUSTOMER",
    actorLabel: "Deniz Yılmaz",
    action: "SHIPPING_ADDRESS_UPDATED",
    resourceType: "subscription",
    resourceId: "sub-tropik-001",
    correlationId: "cor_429ae921",
    createdAt: isoFromNow(-5),
  },
];

let settings: AdminSettings = {
  sendShopifyReceipt: false,
  supportEmail: "destek@tropikalakdeniz.com",
  defaultCurrency: "TRY",
  timezone: "Europe/Istanbul",
  environment: "staging",
  appCheckEnforced: true,
};

const legalDocuments: Record<LegalDocument["type"], LegalDocument> = {
  subscription_terms: {
    type: "subscription_terms",
    version: "placeholder-2026-01",
    active: true,
    publishedAt: isoFromNow(-90),
    requiresLegalReview: true,
    content:
      "Bu geçici metin, düzenli tahsilat, teslimat, fiyat değişikliği ve iptal koşullarının yerini tutmaz. Yayın öncesinde hukuk uzmanı tarafından hazırlanacaktır.",
  },
  privacy_policy: {
    type: "privacy_policy",
    version: "placeholder-2026-01",
    active: true,
    publishedAt: isoFromNow(-90),
    requiresLegalReview: true,
    content:
      "Bu geçici gizlilik metni KVKK aydınlatma yükümlülüklerinin yerini tutmaz. Veri sorumlusu, amaçlar, hukuki sebepler ve haklar yayın öncesinde netleştirilecektir.",
  },
  distance_sales_placeholder: {
    type: "distance_sales_placeholder",
    version: "placeholder-2026-01",
    active: true,
    publishedAt: isoFromNow(-90),
    requiresLegalReview: true,
    content:
      "Bu sayfa mesafeli satış sözleşmesi için ayrılmıştır. Ürün, teslimat, cayma ve iade hükümleri hukuk uzmanı tarafından tamamlanacaktır.",
  },
};

function notFound(resource: string): never {
  throw new Error(`${resource} bulunamadı.`);
}

async function delayed<T>(value: T, signal?: AbortSignal): Promise<T> {
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(resolve, 140);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException("İstek iptal edildi.", "AbortError"));
      },
      { once: true },
    );
  });
  return value;
}

function checkoutUrl(status: "success" | "failure", subscriptionId: string): string {
  const url = new URL("/payment/result", window.location.origin);
  url.searchParams.set("status", status);
  url.searchParams.set("subscriptionId", subscriptionId);
  return url.toString();
}

function cardUpdateResultUrl(subscriptionId: string): string {
  const url = new URL(
    `/subscriptions/${encodeURIComponent(subscriptionId)}/payment-method`,
    window.location.origin,
  );
  url.searchParams.set("updated", "true");
  return url.toString();
}


/* ---------------------------------------------------------------------------
 * Checkout session, product catalog and branding mocks
 * ------------------------------------------------------------------------- */

const MOCK_SESSION_OFFER_ID = "plan-tropik-5kg-monthly-v2";
/** Token -> frozen offer id, mirroring the server's single-use session store. */
const mockSessions = new Map<string, string>([
  ["mock-checkout-session-token-0000000000000000", MOCK_SESSION_OFFER_ID],
]);

let mockBranding: Branding = {
  logoUrl: "https://tropikalakdeniz.com/cdn/shop/files/logo_yeni_boyut.png?height=142",
  logoAlt: "Tropikal Akdeniz",
  primaryColor: "#69C52F",
  primaryHoverColor: "#5AA820",
  primaryTextColor: "#FFFFFF",
  backgroundColor: "#FFFDF9",
  surfaceColor: "#FFFFFF",
  surfaceMutedColor: "#F6F5F1",
  textColor: "#333333",
  mutedTextColor: "#6B6B6B",
  headingColor: "#333333",
  borderColor: "#DFDFDF",
  successColor: "#4D7A50",
  criticalColor: "#D91C1C",
  bodyFontFamily: "Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  headingFontFamily: "Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  buttonRadius: "14px",
  cardRadius: "16px",
  inputRadius: "8px",
  badgeRadius: "40px",
  containerWidth: "1080px",
};

const mockOffers: AdminOffer[] = [
  {
    id: MOCK_SESSION_OFFER_ID,
    version: 2,
    shopifyProductGid: "gid://shopify/Product/8579471638711",
    shopifyVariantGid: "gid://shopify/ProductVariant/48488588673207",
    shopifyVariantId: "48488588673207",
    frequencyUnit: "MONTH",
    intervalCount: 1,
    frequencyLabel: "Ayda bir",
    displayTitle: "Ayda bir",
    displayDescription: "Mevsimin en taze meyveleri her ay kapinizda.",
    badgeText: "En populer",
    regularPriceMinor: 175_000,
    subscriptionPriceMinor: 157_500,
    shippingPriceMinor: 0,
    discountAmountMinor: 17_500,
    discountPercentage: 10,
    active: true,
    defaultForVariant: true,
    sortOrder: 0,
    iyzicoProductReferenceCode: "mock-iyzico-product",
    iyzicoPricingPlanReferenceCode: "mock-pricing-monthly",
  },
  {
    id: "plan-tropik-5kg-biweekly",
    version: 1,
    shopifyProductGid: "gid://shopify/Product/8579471638711",
    shopifyVariantGid: "gid://shopify/ProductVariant/48488588673207",
    shopifyVariantId: "48488588673207",
    frequencyUnit: "WEEK",
    intervalCount: 2,
    frequencyLabel: "2 haftada bir",
    displayTitle: "2 haftada bir",
    regularPriceMinor: 175_000,
    subscriptionPriceMinor: 166_250,
    shippingPriceMinor: 0,
    discountAmountMinor: 8_750,
    discountPercentage: 5,
    active: true,
    defaultForVariant: false,
    sortOrder: 1,
    iyzicoProductReferenceCode: "mock-iyzico-product",
    iyzicoPricingPlanReferenceCode: "mock-pricing-biweekly",
  },
];

const mockCatalogProducts: AdminCatalogProduct[] = [
  {
    shopifyProductGid: "gid://shopify/Product/8579471638711",
    shopifyProductId: "8579471638711",
    title: "Abonelik Kutusu",
    handle: "abonelik-kutusu",
    status: "ACTIVE",
    subscriptionEnabled: true,
    subscriptionOnly: false,
    activeOfferCount: 2,
    variants: [
      {
        shopifyVariantGid: "gid://shopify/ProductVariant/48488588673207",
        shopifyVariantId: "48488588673207",
        title: "5 kg",
        sku: "TA-ABN-5KG",
        regularPriceMinor: 175_000,
        currency: "TRY",
        availableForSale: true,
        inventoryQuantity: 42,
        inventoryPolicy: "DENY",
        subscriptionEnabled: true,
        activeOfferCount: 2,
        offers: mockOffers,
      },
      {
        shopifyVariantGid: "gid://shopify/ProductVariant/48488588705975",
        shopifyVariantId: "48488588705975",
        title: "10 kg",
        sku: "TA-ABN-10KG",
        regularPriceMinor: 300_000,
        currency: "TRY",
        availableForSale: true,
        inventoryQuantity: 18,
        inventoryPolicy: "DENY",
        subscriptionEnabled: false,
        activeOfferCount: 0,
        offers: [],
      },
    ],
  },
];

const mockCheckoutView: CheckoutView = {
  offer: {
    offerId: MOCK_SESSION_OFFER_ID,
    planVersion: 2,
    shopifyVariantId: "48488588673207",
    productTitle: "Abonelik Kutusu",
    variantTitle: "5 kg",
    frequencyUnit: "MONTH",
    intervalCount: 1,
    frequencyLabel: "Ayda bir",
    displayTitle: "Ayda bir",
    displayDescription: "Mevsimin en taze meyveleri her ay kapinizda.",
    badgeText: "En populer",
    regularPriceMinor: 175_000,
    regularPriceLabel: "1.750,00 TL",
    subscriptionPriceMinor: 157_500,
    subscriptionPriceLabel: "1.575,00 TL",
    discountAmountMinor: 17_500,
    discountAmountLabel: "175,00 TL",
    discountPercentage: 10,
    hasDiscount: true,
    shippingIncluded: true,
    currency: "TRY",
  },
  summaryLines: [
    { label: "Teslimat sikligi", value: "Ayda bir" },
    { label: "Normal fiyat", value: "1.750,00 TL", emphasis: "STRIKETHROUGH" },
    { label: "Abonelik indirimi (%10)", value: "-175,00 TL", emphasis: "SAVING" },
    { label: "Kargo", value: "Ücretsiz" },
    { label: "Her teslimatta tahsil edilecek", value: "1.575,00 TL", emphasis: "TOTAL" },
  ],
  contact: {
    firstName: "Deniz",
    lastName: "Yilmaz",
    email: "deniz@example.com",
    phone: "05321234567",
    missingFields: [],
  },
  addresses: {
    choices: [
      {
        id: "gid://shopify/MailingAddress/1",
        source: "SHOPIFY_DEFAULT",
        label: "Fener Mahallesi, Narenciye Sokak No: 12, Muratpasa, Antalya",
        isDefault: true,
        address: defaultAddress,
        missingFields: [],
      },
      {
        id: "gid://shopify/MailingAddress/2",
        source: "SHOPIFY_FIRST",
        label: "Bagdat Caddesi No 42, Kadikoy, Istanbul",
        isDefault: false,
        address: {
          ...defaultAddress,
          address1: "Bagdat Caddesi No 42",
          city: "Kadikoy",
          district: "Kadikoy",
          province: "Istanbul",
          postalCode: "34710",
        },
        missingFields: [],
      },
    ],
    selectedId: "gid://shopify/MailingAddress/1",
    selectedSource: "SHOPIFY_DEFAULT",
    selectedAddress: defaultAddress,
    showPicker: true,
    shopifyAddressAccessDenied: false,
  },
  branding: mockBranding,
  expiresAt: isoFromNow(1),
  changeSelectionUrl:
    "https://tropikalakdeniz.com/products/abonelik-kutusu?variant=48488588673207",
  legal: {
    termsVersion: "2026-08-05-v1",
    privacyVersion: "2026-08-05-v1",
    distanceSalesVersion: "2026-08-05-v1",
  },
};

export function createMockPortalApi(): PortalApi {
  return {
    getCheckoutSession: (token, signal) => {
      // Süresi dolmuş oturum ekranı mock üzerinden de görülebilsin diye: bu jeton
      // sunucunun 401 cevabını taklit eder, böylece hata kartı test edilebilir kalır.
      if (token.startsWith("expired")) {
        return Promise.reject(new ApiError("Oturum bulunamadı.", 401, "CHECKOUT_SESSION_NOT_FOUND"));
      }
      // Misafir ödemesi: arkasında hesap yok, iletişim ve adres boş gelir.
      if (token.startsWith("guest")) {
        return delayed(
          {
            ...mockCheckoutView,
            guest: true,
            contact: { missingFields: ["firstName", "lastName", "email", "phone"] },
            addresses: {
              choices: [],
              selectedSource: "EMPTY",
              showPicker: false,
              shopifyAddressAccessDenied: false,
            },
          },
          signal,
        );
      }
      if (!mockSessions.has(token)) mockSessions.set(token, MOCK_SESSION_OFFER_ID);
      return delayed(mockCheckoutView, signal);
    },
    exchangeCode: (_code, signal) =>
      delayed({ customToken: "mock-custom-token", redirectPath: "/subscriptions" }, signal),
    listPlans: (signal) => delayed(plans.filter((plan) => plan.active), signal),
    getPlan: (planId, signal) => {
      const plan = plans.find((item) => item.id === planId);
      return delayed(plan ?? notFound("Plan"), signal);
    },
    getLegalDocument: (type, signal) => delayed(legalDocuments[type], signal),
    getPaymentResult: (subscriptionId, signal) =>
      delayed(
        {
          subscriptionId,
          status: "SUCCEEDED",
          message: "Ödemeniz doğrulandı ve ilk kutunuz hazırlık sırasına alındı.",
          accountUrl: "https://tropikalakdeniz.com/account",
          storefrontUrl: "https://tropikalakdeniz.com",
        },
        signal,
      ),
    listSubscriptions: (signal) =>
      delayed(subscriptions.filter((item) => item.customerUid === customerUid), signal),
    getSubscription: (subscriptionId, signal) => {
      const subscription = subscriptions.find((item) => item.id === subscriptionId);
      return delayed(subscription ?? notFound("Abonelik"), signal);
    },
    initializeSubscription: async (input) => {
      // The mock resolves the session token the same way the server does: the plan comes
      // from the frozen session, never from the request body.
      const session = mockSessions.get(input.checkoutSessionToken) ?? MOCK_SESSION_OFFER_ID;
      const plan = plans.find((item) => item.id === session && item.active);
      if (!plan) return notFound("Plan");
      const id = `sub-mock-${crypto.randomUUID()}`;
      subscriptions = [
        ...subscriptions,
        {
          id,
          customerUid,
          reference: `TA-MOCK-${Date.now()}`,
          status: "CHECKOUT_PENDING",
          providerStatus: "PENDING",
          plan,
          amountMinor: plan.amountMinor,
          currency: "TRY",
          shippingAddress: input.shippingAddress,
          cycles: [],
          customer: {
            name: `${input.shippingAddress.firstName} ${input.shippingAddress.lastName}`,
            email: "deniz@example.com",
            shopifyCustomerId: "7845129630",
          },
          webhookEvents: [],
          auditEntries: [],
        },
      ];
      return delayed({ subscriptionId: id, checkoutUrl: checkoutUrl("success", id) });
    },
    updateAddress: async (subscriptionId, input) => {
      const index = subscriptions.findIndex((item) => item.id === subscriptionId);
      if (index < 0) return notFound("Abonelik");
      const current = subscriptions[index]!;
      subscriptions[index] = { ...current, shippingAddress: input };
      return delayed({ success: true, message: "Teslimat adresiniz güncellendi." });
    },
    initializeCardUpdate: (subscriptionId) =>
      delayed({
        subscriptionId,
        checkoutUrl: cardUpdateResultUrl(subscriptionId),
      }),
    cancelSubscription: async (subscriptionId) => {
      const index = subscriptions.findIndex((item) => item.id === subscriptionId);
      if (index < 0) return notFound("Abonelik");
      const current = subscriptions[index]!;
      subscriptions[index] = {
        ...current,
        status: "CANCELED",
        providerStatus: "CANCELED",
        canceledAt: new Date().toISOString(),
      };
      return delayed({
        success: true,
        message: "Aboneliğiniz iptal edildi; önceki siparişleriniz değişmedi.",
      });
    },
    admin: {
      getDashboard: (signal) =>
        delayed(
          {
            activeSubscriptions: 128,
            pastDueSubscriptions: 7,
            successfulPaymentsToday: 14,
            monthlyRevenueMinor: 4_382_400,
            pendingOrderSync: 3,
            failedOrderSync: orderSyncIssues.length,
            recentWebhookErrors: [
              {
                id: "evt-admin-01",
                eventType: "subscription.order.failure",
                occurredAt: isoFromNow(-0.4),
                message: "Subscription detail sorgusu geçici olarak zaman aşımına uğradı.",
                correlationId: "cor_8db34911",
              },
            ],
          },
          signal,
        ),
      listPlans: (signal) => delayed(plans, signal),
      getPlanCatalog: (signal) =>
        delayed(
          {
            shopifyProducts,
            iyzicoProducts,
            iyzicoPricingPlans,
          },
          signal,
        ),
      upsertPlan: async (input: AdminPlanInput) => {
        const variant = shopifyProducts
          .flatMap((product) => product.variants)
          .find((item) => item.id === input.shopifyVariantGid);
        if (!variant) return notFound("Shopify varyantı");
        const pricingPlan = iyzicoPricingPlans.find(
          (item) => item.referenceCode === input.iyzicoPricingPlanReferenceCode,
        );
        if (!pricingPlan) return notFound("iyzico fiyat planı");
        if (
          pricingPlan.amountMinor !== input.amountMinor ||
          variant.amountMinor + input.shippingPriceMinor !== input.amountMinor
        ) {
          throw new Error("Shopify, kargo ve iyzico tutarları birbiriyle eşleşmiyor.");
        }
        const existingIndex = input.planId
          ? plans.findIndex((item) => item.id === input.planId)
          : -1;
        const next: AdminPlan = {
          id: input.planId ?? `plan-mock-${plans.length + 1}`,
          name: input.name,
          ...(input.description ? { description: input.description } : {}),
          variantLabel: variant.title,
          ...(variant.sku ? { sku: variant.sku } : {}),
          frequency: input.frequency,
          intervalCount: input.intervalCount,
          amountMinor: input.amountMinor,
          currency: input.currency,
          shippingIncluded: true,
          shippingPriceMinor: input.shippingPriceMinor,
          shopifyProductId: input.shopifyProductId,
          shopifyProductGid: input.shopifyProductGid,
          shopifyVariantId: input.shopifyVariantId,
          shopifyVariantGid: input.shopifyVariantGid,
          iyzicoProductReferenceCode: input.iyzicoProductReferenceCode,
          iyzicoPricingPlanReferenceCode: input.iyzicoPricingPlanReferenceCode,
          active: input.active,
          version: existingIndex < 0 ? 1 : plans[existingIndex]!.version + 1,
        };
        if (existingIndex < 0) plans.push(next);
        else plans[existingIndex] = next;
        return delayed(next);
      },
      createIyzicoProduct: async (input) => {
        const product: IyzicoCatalogProduct = {
          referenceCode: `iyzi-product-mock-${iyzicoProducts.length + 1}`,
          name: input.name,
          ...(input.description ? { description: input.description } : {}),
          status: "ACTIVE",
        };
        iyzicoProducts = [...iyzicoProducts, product];
        return delayed(product);
      },
      createIyzicoPricingPlan: async (input) => {
        if (!iyzicoProducts.some((product) => product.referenceCode === input.productReferenceCode)) {
          return notFound("iyzico ürünü");
        }
        const pricingPlan: IyzicoCatalogPricingPlan = {
          referenceCode: `iyzi-plan-mock-${iyzicoPricingPlans.length + 1}`,
          productReferenceCode: input.productReferenceCode,
          name: input.name,
          amountMinor: input.amountMinor,
          currency: input.currency,
          paymentInterval: input.paymentInterval,
          paymentIntervalCount: input.paymentIntervalCount,
          status: "ACTIVE",
        };
        iyzicoPricingPlans = [...iyzicoPricingPlans, pricingPlan];
        return delayed(pricingPlan);
      },
      syncPlans: () =>
        delayed({ success: true, message: "Shopify ürün ve varyant senkronizasyonu kuyruğa alındı." }),
      setPlanActive: async (planId, active) => {
        const index = plans.findIndex((item) => item.id === planId);
        if (index < 0) return notFound("Plan");
        plans[index] = { ...plans[index]!, active };
        subscriptions = subscriptions.map((subscription) =>
          subscription.plan.id === planId
            ? { ...subscription, plan: plans[index]! }
            : subscription,
        );
        return delayed({
          success: true,
          message: active ? "Plan yeni aboneliklere açıldı." : "Plan yeni aboneliklere kapatıldı.",
        });
      },
      listSubscriptions: (filters: AdminSubscriptionFilters = {}, signal) => {
        const query = filters.customer?.toLocaleLowerCase("tr-TR");
        const filtered = subscriptions.filter((subscription) => {
          if (filters.status && subscription.status !== filters.status) return false;
          if (filters.planId && subscription.plan.id !== filters.planId) return false;
          if (
            filters.iyzicoReference &&
            !subscription.reference.toLocaleLowerCase("tr-TR").includes(
              filters.iyzicoReference.toLocaleLowerCase("tr-TR"),
            )
          ) {
            return false;
          }
          if (filters.from && subscription.startedAt && subscription.startedAt < filters.from) return false;
          if (filters.to && subscription.startedAt && subscription.startedAt > filters.to) return false;
          if (
            query &&
            !`${subscription.customer.name} ${subscription.customer.email} ${subscription.customer.shopifyCustomerId}`
              .toLocaleLowerCase("tr-TR")
              .includes(query)
          ) {
            return false;
          }
          return true;
        });
        return delayed(filtered, signal);
      },
      getSubscription: (subscriptionId, signal) => {
        const subscription = subscriptions.find((item) => item.id === subscriptionId);
        return delayed(subscription ?? notFound("Abonelik"), signal);
      },
      listPaymentIssues: (signal) => delayed(paymentIssues, signal),
      retryPayment: async (cycleId) => {
        paymentIssues = paymentIssues.filter((issue) => issue.cycleId !== cycleId);
        return delayed({ success: true, message: "Tahsilat yeniden deneme işlemi güvenle başlatıldı." });
      },
      resolveAmbiguousPaymentRetry: async (cycleId) => {
        paymentIssues = paymentIssues.map((issue) =>
          issue.cycleId === cycleId
            ? { ...issue, retryAllowed: true, reviewRequired: false }
            : issue,
        );
        return delayed({
          success: true,
          message: "Sağlayıcıda yeni tahsilat olmadığı teyit edilen kayıt yeniden denemeye açıldı.",
        });
      },
      listOrderSyncIssues: (signal) => delayed(orderSyncIssues, signal),
      retryOrderSync: async (cycleId) => {
        orderSyncIssues = orderSyncIssues.filter((issue) => issue.cycleId !== cycleId);
        return delayed({ success: true, message: "Mevcut ödeme için sipariş senkronizasyonu kuyruğa alındı." });
      },
      markOrderSyncPermanent: async (cycleId) => {
        orderSyncIssues = orderSyncIssues.filter((issue) => issue.cycleId !== cycleId);
        return delayed({ success: true, message: "Kayıt operasyon incelemesine alındı." });
      },
      listCustomers: (signal) => delayed(customers, signal),
      listAudit: (signal) => delayed(auditEntries, signal),
      getSettings: (signal) => delayed(settings, signal),
      updateSettings: async (input) => {
        settings = { ...settings, ...input };
        return delayed(settings);
      },
      getProductCatalog: (_params, signal) =>
        delayed({ products: mockCatalogProducts, lastSyncedAt: isoFromNow(0) }, signal),
      upsertOffer: async (input) => {
        const existing = mockOffers.find((offer) => offer.id === input.offerId);
        const base = existing ?? mockOffers[0]!;
        const saved: AdminOffer = {
          ...base,
          id: input.offerId ?? `plan-mock-${crypto.randomUUID()}`,
          shopifyProductGid: input.shopifyProductGid,
          shopifyVariantGid: input.shopifyVariantGid,
          frequencyUnit: input.frequencyUnit,
          intervalCount: input.intervalCount,
          subscriptionPriceMinor: input.subscriptionPriceMinor,
          shippingPriceMinor: input.shippingPriceMinor,
          active: input.active,
          defaultForVariant: input.defaultForVariant,
          sortOrder: input.sortOrder,
          iyzicoProductReferenceCode: input.iyzicoProductReferenceCode,
          iyzicoPricingPlanReferenceCode: input.iyzicoPricingPlanReferenceCode,
          ...(input.displayTitle ? { displayTitle: input.displayTitle } : {}),
          ...(input.badgeText ? { badgeText: input.badgeText } : {}),
        };
        return delayed({ offer: saved });
      },
      repriceOffer: async (offerId, input) => {
        const index = mockOffers.findIndex((item) => item.id === offerId);
        const offer = mockOffers[index] ?? mockOffers[0]!;
        const regular = offer.regularPriceMinor;
        const discountAmountMinor = Math.max(0, regular - input.newSubscriptionPriceMinor);
        const successor: AdminOffer = {
          ...offer,
          id: `plan-mock-${crypto.randomUUID()}`,
          version: 1,
          subscriptionPriceMinor: input.newSubscriptionPriceMinor,
          discountAmountMinor,
          discountPercentage:
            regular === 0 ? 0 : Math.round((discountAmountMinor / regular) * 100),
          iyzicoPricingPlanReferenceCode: `iyzico-plan-mock-${crypto.randomUUID().slice(0, 8)}`,
        };
        if (index >= 0) mockOffers[index] = successor;
        return delayed({
          offer: successor,
          repricedFrom: offer.id,
          providerPlanCreated: successor.iyzicoPricingPlanReferenceCode,
        });
      },
      syncOfferShelfPrice: async (offerId) => {
        const offer = mockOffers.find((item) => item.id === offerId) ?? mockOffers[0]!;
        return delayed({ offer });
      },
      setOfferState: async (offerId, input) => {
        const offer = mockOffers.find((item) => item.id === offerId) ?? mockOffers[0]!;
        return delayed({
          ...offer,
          ...(input.active === undefined ? {} : { active: input.active }),
          ...(input.defaultForVariant === undefined
            ? {}
            : { defaultForVariant: input.defaultForVariant }),
          ...(input.sortOrder === undefined ? {} : { sortOrder: input.sortOrder }),
        });
      },
      setVariantSubscription: async (_gid, enabled) =>
        delayed({
          success: true,
          message: enabled ? "Varyant aboneligi acildi." : "Varyant aboneligi kapatildi.",
        }),
      setProductSubscription: async (_gid, enabled) =>
        delayed({
          success: true,
          message: enabled ? "Urun aboneligi acildi." : "Urun aboneligi kapatildi.",
        }),
      getBranding: (signal) => delayed(mockBranding, signal),
      updateBranding: async (input) => {
        mockBranding = input;
        return delayed(mockBranding);
      },
      syncBrandingFromTheme: async () =>
        delayed({
          branding: mockBranding,
          appliedFields: ["primaryColor", "bodyFontFamily"],
          skippedFields: ["containerWidth"],
          sourceUrl: "https://tropikalakdeniz.com/",
        }),
    },
  };
}
